import { useState } from "react";
import { ArrowRight, Loader2, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../translations";
import { useHome } from "../translations/home";
import { lastCtaSection } from "../lib/ctaTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Formulaire de demande de démo (3 champs). Utilisé dans le CTA final de la home et dans le footer. */
export const DemoForm = ({ ctaLabel, micro }) => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const home = useHome(language);

  const [formData, setFormData] = useState({ email: "", company: "", company_size: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error(home.form.emptyEmail);
      return;
    }
    if (!formData.company || !formData.company_size) {
      toast.error(
        language === "fr"
          ? "Il nous manque votre entreprise et sa taille."
          : language === "es"
          ? "Nos falta su empresa y su tamaño."
          : "We still need your company and its size."
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/demo-request`, { ...formData, language, cta_section: lastCtaSection() });
      setIsSubmitted(true);
      toast.success(
        language === "fr"
          ? "Merci ! Nous vous recontacterons rapidement."
          : language === "es"
          ? "¡Gracias! Te contactaremos pronto."
          : "Thank you! We'll be in touch shortly."
      );
    } catch (error) {
      toast.error(
        language === "fr"
          ? "Une erreur est survenue. Veuillez réessayer."
          : language === "es"
          ? "Algo salió mal. Por favor intente de nuevo."
          : "Something went wrong. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12" data-testid="form-success">
        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-[#34D399]" />
        </div>
        <h3 className="font-semibold text-xl text-white mb-2">{home.success.title}</h3>
        <p className="text-[#9098A3]">{home.success.message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label htmlFor="email" className="text-[#E4EEFC] mb-2 block">
          {t.footer.form.email} *
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
          className="bg-[#0A2342]/70 border-white/20 text-white placeholder:text-[#5E6C83] focus:border-blue-500"
          placeholder="prenom.nom@entreprise.com"
          required
          data-testid="input-email"
        />
      </div>

      <div>
        <Label htmlFor="company" className="text-[#E4EEFC] mb-2 block">
          {t.footer.form.company} *
        </Label>
        <Input
          id="company"
          name="company"
          value={formData.company}
          onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))}
          className="bg-[#0A2342]/70 border-white/20 text-white placeholder:text-[#5E6C83] focus:border-blue-500"
          placeholder="Acme Inc."
          required
          data-testid="input-company"
        />
      </div>

      <div>
        <Label htmlFor="company_size" className="text-[#E4EEFC] mb-2 block">
          {home.form.sizeQuestion} *
        </Label>
        <Select onValueChange={(v) => setFormData((p) => ({ ...p, company_size: v }))} value={formData.company_size}>
          <SelectTrigger className="bg-[#0A2342]/70 border-white/20 text-white focus:border-blue-500" data-testid="select-company-size">
            <SelectValue placeholder={t.footer.form.selectSize} />
          </SelectTrigger>
          <SelectContent className="bg-[#0A2342] border-white/20">
            {t.footer.companySizes.map((size) => (
              <SelectItem key={size} value={size} className="text-white hover:bg-slate-800">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-md px-6 py-4 bg-[#0047AB] text-white font-semibold hover:bg-[#0B3F8F] transition-all cta-glow text-sm sm:text-base whitespace-normal"
        data-testid="submit-demo-button"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t.footer.form.submitting}
          </>
        ) : (
          <>
            {ctaLabel || home.cta.label}
            <ArrowRight className="ml-2 h-5 w-5" />
          </>
        )}
      </Button>

      <p className="text-xs text-[#9098A3] leading-relaxed" data-testid="demo-form-micro">
        {micro || home.final.micro}
      </p>
    </form>
  );
};

export default DemoForm;
